import { __axios } from '@/Services/api/modules/interceptors';

import { CommitSummary, ManifestEntry, ManifestMismatch } from './types';

interface AxiosResultWrapper<T> {
    code?: number;
    data?: T;
    message?: string;
    errors?: Record<string, string[]>;
}

const FOLDER_IMAGE_PATH = 'v1/import/folder/image';
const FOLDER_COMMIT_PATH = 'v1/import/folder/commit';
const FOLDER_COMMIT_OVERWRITE_PATH = 'v1/import/folder/commit-overwrite';
const FOLDER_PROPOSAL_COMMIT_PATH = 'v1/import/folder/proposal-commit';

export async function uploadFolderImage(opts: {
    file: File;
    batchId: string;
    rowIndex: number;
    filename: string;
    uploadId: string;
    sha256: string;
}): Promise<{ ok: true; status: number } | { ok: false; status: number; message?: string }> {
    const fd = new FormData();
    fd.append('file', opts.file);
    fd.append('batch_id', opts.batchId);
    fd.append('row_index', String(opts.rowIndex));
    fd.append('filename', opts.filename);

    try {
        const res = (await __axios.request({
            method: 'post',
            url: '/' + FOLDER_IMAGE_PATH,
            data: fd,
            headers: {
                'Content-Type': 'multipart/form-data',
                'Idempotency-Key': opts.uploadId,
                'X-Content-SHA256': opts.sha256,
            },
        })) as unknown as AxiosResultWrapper<unknown>;

        if (res.code === 200 || res.code === 201) {
            return { ok: true, status: res.code };
        }
        return { ok: false, status: res.code ?? 0, message: res.message };
    } catch (e) {
        return { ok: false, status: 0, message: 'network error' };
    }
}

export async function commitFolder(opts: {
    xls: File;
    batchId: string;
    manifest: ManifestEntry[];
    overwrite: boolean;
}): Promise<{ ok: true; summary: CommitSummary } | { ok: false; status: number; mismatch?: ManifestMismatch; message?: string }> {
    const path = opts.overwrite ? FOLDER_COMMIT_OVERWRITE_PATH : FOLDER_COMMIT_PATH;

    const fd = new FormData();
    fd.append('xls', opts.xls);
    fd.append('batch_id', opts.batchId);
    fd.append('manifest', JSON.stringify(opts.manifest));

    try {
        const res = (await __axios.request({
            method: 'post',
            url: '/' + path,
            data: fd,
            headers: { 'Content-Type': 'multipart/form-data' },
        })) as unknown as AxiosResultWrapper<CommitSummary>;

        if (res.code === 200 && res.data) {
            return { ok: true, summary: res.data };
        }

        if (res.code === 409 && res.data) {
            return { ok: false, status: 409, mismatch: res.data as unknown as ManifestMismatch };
        }

        return { ok: false, status: res.code ?? 0, message: res.message };
    } catch (e) {
        return { ok: false, status: 0, message: 'network error' };
    }
}

export async function commitProposalFolder(opts: {
    xls: File;
    batchId: string;
    manifest: ManifestEntry[];
}): Promise<{ ok: true; summary: CommitSummary } | { ok: false; status: number; mismatch?: ManifestMismatch; message?: string }> {
    const fd = new FormData();
    fd.append('xls', opts.xls);
    fd.append('batch_id', opts.batchId);
    fd.append('manifest', JSON.stringify(opts.manifest));

    try {
        const res = (await __axios.request({
            method: 'post',
            url: '/' + FOLDER_PROPOSAL_COMMIT_PATH,
            data: fd,
            headers: { 'Content-Type': 'multipart/form-data' },
        })) as unknown as AxiosResultWrapper<CommitSummary>;

        if (res.code === 200 && res.data) {
            return { ok: true, summary: res.data };
        }

        if (res.code === 409 && res.data) {
            return { ok: false, status: 409, mismatch: res.data as unknown as ManifestMismatch };
        }

        return { ok: false, status: res.code ?? 0, message: res.message };
    } catch (e) {
        return { ok: false, status: 0, message: 'network error' };
    }
}
